<!-- eslint-disable vue/html-self-closing -->
<template>
  <section
    v-if="discussions?.length > 0"
    class="merge-request-discussions"
  >
    <transition-group
      class="merge-request-discussions__list"
      name="discussions-list"
      tag="div"
    >
      <div
        v-for="discussion in discussions"
        :key="discussion.id"
        class="merge-request-discussions__discussion"
      >
        <div class="merge-request-discussions__header">
          <a
            class="author"
            :href="'/user/' + discussion?.author?.username"
          >
            <img
              :src="discussion?.author?.avatar"
              class="avatar"
              height="22px"
              width="22px"
            />
            <b class="fullname">{{ discussion?.author?.fullName }}</b>
            <span class="username">@{{ discussion?.author?.username }}</span>
          </a>
          <div class="info">{{ $t('discussion_created') }}</div>
        </div>
        <div class="merge-request-discussions__content">
          <div
            v-if="discussion?.fileDiffId && discussion?.filePath"
            class="merge-request-discussions__file"
          >
            <div class="header">
              <i class="gf-icon gf-icon-file"></i>
              <div class="filepath">
                <span
                  v-if="
                    discussion?.oldPath !== '/dev/null' &&
                    discussion?.oldPath !== discussion?.filePath
                  "
                  class="oldpath"
                >
                  <i class="gf-icon gf-icon-arrow-right"></i>
                  <span class="path">
                    {{ discussion.oldPath }}
                  </span>
                </span>
                <router-link
                  class="newpath"
                  tag="a"
                  :to="{
                    name: 'merge-request-item-diff',
                    hash: '#' + discussion?.fileDiffId
                  }"
                >
                  {{ discussion.filePath }}
                </router-link>
              </div>
            </div>
            <div class="code">
              <table
                class="table table-sm mb-0 table-borderless"
                @copy.prevent="onCodeCopy"
              >
                <commit-diff-item-line
                  v-for="line in discussion.highLightLines"
                  :key="line.id"
                  :line="line"
                />
              </table>
            </div>
          </div>
          <div class="merge-request-discussions__replies">
            <merge-request-discussion-reply
              class="reply"
              :reply="discussion"
              @deleteDiscussion="onDiscussionDelete"
            />

            <TransitionGroup
              name="discussion-reply"
              tag="div"
            >
              <merge-request-discussion-reply
                v-for="reply in discussion.replies"
                :key="reply.id"
                class="reply"
                :reply="reply"
                @deleteDiscussion="onDiscussionReplyDelete"
              />
            </TransitionGroup>
            <div class="create-reply">
              <merge-request-discussion-reply-form
                :is-reply="true"
                :discussion="discussion"
                @closeDiscussion="closeDiscussion(discussion)"
                @submit="onSubmitReply($event, discussion)"
              />
            </div>
          </div>
        </div>
      </div>
    </transition-group>
  </section>
  <section
    v-else-if="discussions?.length === 0"
    class="merge-request-discussions"
  >
    <div class="merge-request-discussions__empty">
      {{ $t('disscussion_none') }}
    </div>
  </section>
</template>

<script>
import CommitDiffItemLine from '@shared/shared-components/commit-diff/commit-diff-item-line';
// eslint-disable-next-line max-len
import MergeRequestDiscussionReply from '@shared/modules/merge-request/components/item/MergeRequestDiscussionReply';
import MergeRequestService from '@shared/modules/merge-request/merge-request.service';
// eslint-disable-next-line max-len
import MergeRequestDiscussionReplyForm from '@shared/modules/merge-request/forms/item/MergeRequestDiscussionReplyForm';
import HelperService from '@shared/services/helper.service';
import MergeRequestMessages from '@shared/modules/merge-request/mergeRequestMessages';

export default {
  name: 'MergeRequestDiscussions',
  components: {
    CommitDiffItemLine,
    MergeRequestDiscussionReply,
    MergeRequestDiscussionReplyForm
  },
  computed: {
    discussions() {
      return this.$store.getters.getMergeRequestsDiscussions;
    },
    alias() {
      return this.$route.params?.alias;
    },
    projectAlias() {
      return this.$route.params?.projectAlias;
    },
    mergeRequestId() {
      return this.$route.params?.id;
    },
    mergeRequest() {
      return this.$store.getters.getSelectedMergeRequest;
    }
  },
  methods: {
    onCodeCopy(e) {
      e.clipboardData.setData('text/plain', document.getSelection());
      e.preventDefault();
    },
    onDiscussionDelete(data) {
      this.$store.dispatch('deleteMergeRequestDiscussionById', data?.id);
      MergeRequestService.getMergeRequest(this.alias, this.projectAlias, this.mergeRequestId);
    },
    onDiscussionReplyDelete(data) {
      this.$store.dispatch('deleteMergeRequestDiscussionReplyById', data?.id);
    },
    getMergeRequest() {
      MergeRequestService.getMergeRequest(this.alias, this.projectAlias, this.mergeRequestId);
    },
    closeDiscussion(discussion) {
      MergeRequestService.resolveDiscussion(
        this.alias,
        this.projectAlias,
        this.mergeRequestId,
        discussion
      ).then(() => {
        this.getMergeRequest();
      });
    },
    onSubmitReply(data, discussion) {
      MergeRequestService.createDiscussion(
        this.alias,
        this.projectAlias,
        HelperService.generateDiscussionObject(
          {
            data,
            discussion,
            file: { newPath: discussion.filePath, oldPath: discussion.oldPath },
            line: { id: discussion.lineDiffId }
          },
          this.mergeRequest
        )
      ).then(() => {
        this.getMergeRequest();
      });
    }
  },
  i18n: {
    messages: MergeRequestMessages
  }
};
</script>

<style lang="scss">
$this: '.merge-request-discussions';

#{$this} {
  #{$this}__list {
    display: flex;
    flex-direction: column;
    gap: 24px;

    #{$this}__discussion {
      display: flex;
      flex-direction: column;
      gap: 8px;

      #{$this}__header {
        display: flex;
        align-items: center;
        gap: 4px;

        @include theme.max-md {
          flex-wrap: wrap;
        }

        & > .author {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: nowrap;
          white-space: nowrap;
          cursor: pointer;

          @include theme.max-md {
            flex-wrap: wrap;
          }

          &:hover {
            & > * {
              color: var(--primary) !important;
            }
          }

          & > .avatar {
            border-radius: 11px;
          }
          & > .fullname {
            max-width: 250px;
            color: var(--text);
            overflow: hidden;
            text-overflow: ellipsis;

            @include theme.max-xxl {
              max-width: 200px;
            }
            @include theme.max-xl {
              max-width: 135px;
            }
            @include theme.max-md {
              max-width: 145px;
            }
            @include theme.max-sm {
              max-width: 260px;
            }
          }
          & > .username {
            max-width: 250px;
            color: var(--text-muted);
            overflow: hidden;
            text-overflow: ellipsis;

            @include theme.max-xxl {
              max-width: 200px;
            }
            @include theme.max-xl {
              max-width: 135px;
            }
            @include theme.max-md {
              max-width: 145px;
            }
            @include theme.max-sm {
              max-width: 280px;
            }
          }
        }
        & > .info {
          white-space: nowrap;
        }
      }

      #{$this}__content {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--border-color);
        border-radius: 6px;

        #{$this}__file {
          display: flex;
          flex-direction: column;

          & > .header {
            padding: 8px 16px;
            gap: 4px;
            background-color: var(--light);
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;
            border-bottom: 1px solid var(--border-color);

            & > .filepath {
              max-width: calc(100% - 22px);
              display: flex;
              flex-wrap: wrap;
              gap: 4px;

              & > .oldpath {
                max-width: 100%;
                direction: rtl;
                gap: 4px;

                & > .path {
                  max-width: calc(100% - 24px);
                  overflow: hidden;
                  display: block;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }
              }
              & > .newpath {
                max-width: 100%;
                display: block;
                direction: rtl;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
          & > .code {
            overflow-wrap: anywhere;
            border-bottom: 1px solid var(--border-color);
          }
        }
        #{$this}__replies {
          & > .reply {
            border-top: none !important;
          }

          & .reply {
            border-top: 1px solid var(--border-color);
          }
          & > .create-reply {
          }
        }
      }
    }
  }

  #{$this}__empty {
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary);
    border: 1px solid var(--primary);
    border-radius: 6px;
    background-color: color-mix(in srgb, var(--primary) 10%, var(--white));
  }

  .discussions-list-enter-from,
  .discussions-list-leave-to {
    animation: gf-hide-discussion 0.2s ease-in-out;
  }

  .discussion-reply-enter-from,
  .discussion-reply-leave-to {
    animation: gf-hide-discussion 0.25s ease-in-out;
  }

  @keyframes gf-hide-discussion {
    0% {
      max-height: 2000px;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 0;
      max-height: 0;
    }
  }
}
</style>
